/// <amd-module name="Repos/Views/Branches/Provider/BranchTreeProvider" />
import { EnhancedGitRef } from "Data/EnhancedGitRef";
import {
    GitBranchStats,
    GitCommitRef,
    GitRef,
    GitRefFavorite,
    GitRepository,
    GitVersionDescriptor,
    GitVersionOptions,
    GitVersionType,
    RefFavoriteType
} from "Repos/Common/Contracts/Git";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { branchNameToRef, refToBranchName } from "Repos/Common/Util/Ref";
import { branchToVersionDescriptor } from "Repos/Common/Util/Version";
import { ISparseTreeProvider } from "Repos/SparseTree/SparseTreeProvider.Types";
import { IObservableValue, IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IGitRestClient } from "../RestClient/Git";
import { IBranchTreeProvider } from "./BranchTreeProvider.Types";

export class BranchTreeProvider implements IBranchTreeProvider {
    private readonly baseProviderInternal: ISparseTreeProvider<EnhancedGitRef>;
    private compareBranch: EnhancedGitRef | undefined;
    private defaultBranch: EnhancedGitRef | undefined;
    private isUserSetCompareBranch: boolean | undefined;
    private readonly routingService: IReposRoutingService;
    private readonly gitRestClient: IGitRestClient;
    private folderPathToFavorite: { [key: string]: IObservableValue<GitRefFavorite | undefined> };
    private policyPaths: Set<string>;
    private readonly repository: GitRepository | undefined;
    private readonly repositoryName: string;
    private readonly projectName: string;
    private readonly isTargetBranchUpdates2Enabled: boolean = false;

    constructor(
        baseProvider: ISparseTreeProvider<EnhancedGitRef>,
        initialCompareBranch: EnhancedGitRef | undefined,
        initialDefaultBranch: EnhancedGitRef | undefined,
        reposRoutingService: IReposRoutingService,
        gitRestClient: IGitRestClient,
        favorites: GitRefFavorite[],
        policyPaths: string[],
        repository: GitRepository | undefined,
        repositoryName: string,
        projectName: string,
        isTargetBranchUpdates2Enabled: boolean,
    ) {
        this.initialize(initialCompareBranch, initialDefaultBranch, favorites, policyPaths);
        this.baseProviderInternal = baseProvider;
        this.routingService = reposRoutingService;
        this.gitRestClient = gitRestClient;
        this.repository = repository;
        this.repositoryName = repositoryName;
        this.projectName = projectName;
        this.isTargetBranchUpdates2Enabled = isTargetBranchUpdates2Enabled;
    }

    public get baseProvider() {
        return this.baseProviderInternal;
    }

    public createEnhancedRef = (gitRef: GitRef, metadata: GitCommitRef) => {
        const statsObservable = new ObservableValue<GitBranchStats | undefined>(undefined);
        const refVersion = branchToVersionDescriptor(refToBranchName(gitRef.name));
        const enhancedRef: EnhancedGitRef = {
            rootRef: gitRef,
            metadata: metadata,
            isUserCreated: true,
            isCompare: false,
            isDefault: false,
            refUrl: this.routingService.getFilesUrl(this.repositoryName, this.projectName, undefined, refVersion),
            policiesUrl: this.routingService.getBranchPolicyUrl(this.repositoryName, this.projectName, gitRef.name),
            securityUrl: this.routingService.getBranchSecurityUrl(this.repository!.id, this.projectName, gitRef.name),
            compareUrl:
                this.compareBranch &&
                this.routingService.getBranchCompareUrl(
                    this.repositoryName,
                    this.projectName,
                    branchToVersionDescriptor(refToBranchName(this.compareBranch.rootRef.name)),
                    refVersion
                ),
            reverseCompareUrl:
                this.compareBranch &&
                this.routingService.getBranchCompareUrl(
                    this.repositoryName,
                    this.projectName,
                    refVersion,
                    branchToVersionDescriptor(refToBranchName(this.compareBranch.rootRef.name))
                ),
            commitUrl: this.routingService.getCommitUrl(this.repositoryName, this.projectName, gitRef.objectId, branchNameToRef(gitRef.name)),
            historyUrl: this.routingService.getCommitsUrl(this.repositoryName, this.projectName, {
                version: refToBranchName(gitRef.name),
                versionType: GitVersionType.Branch,
                versionOptions: GitVersionOptions.None
            }),
            stats: statsObservable
        };

        if (this.repository && this.compareBranch) {
            this.gitRestClient
                .getBranchStatsBatch(
                    {
                        baseCommit: {
                            version: this.compareBranch.rootRef.objectId,
                            versionType: GitVersionType.Commit,
                            versionOptions: GitVersionOptions.None
                        },
                        targetCommits: [
                            {
                                version: gitRef.objectId,
                                versionType: GitVersionType.Commit,
                                versionOptions: GitVersionOptions.None
                            }
                        ]
                    },
                    this.repository.id,
                    this.projectName
                )
                .then(result => {
                    if (result[0]) {
                        statsObservable.value = result[0];
                    }
                });
        }

        return enhancedRef;
    };

    public getCompareBranch = () => {
        return this.compareBranch;
    };

    public getDefaultBranch = () => {
        return this.defaultBranch;
    };

    public getFolderFavorite = (path: string): IReadonlyObservableValue<GitRefFavorite | undefined> => {
        path = branchNameToRef(path);

        if (this.folderPathToFavorite[path]) {
            return this.folderPathToFavorite[path];
        }

        const newFavorite = new ObservableValue<undefined>(undefined);
        this.folderPathToFavorite[path] = newFavorite;
        return newFavorite;
    };

    public initialize = (
        initialCompareBranch: EnhancedGitRef | undefined,
        initialDefaultBranch: EnhancedGitRef | undefined,
        favorites: GitRefFavorite[],
        policyPaths: string[],
        isUserSetCompareBranch?: boolean
    ) => {
        this.compareBranch = initialCompareBranch;
        this.defaultBranch = initialDefaultBranch;

        this.folderPathToFavorite = {};
        favorites.filter(x => x.type === RefFavoriteType.Folder).forEach(x => (this.folderPathToFavorite[x.name] = new ObservableValue(x)));

        this.policyPaths = new Set(policyPaths.map(refToBranchName));
        this.isUserSetCompareBranch = this.isUserSetCompareBranch || isUserSetCompareBranch;
    };

    public pathHasPolicy = (path: string) => {
        if (this.policyPaths.has(path)) {
            return true;
        }

        while (path !== "") {
            // Add this to test parent folders
            if (this.policyPaths.has(path + "/*")) {
                return true;
            }

            path = path.substring(0, Math.max(0, path.lastIndexOf("/")));
        }

        return false;
    };

    public setCompareBranch = (branch: EnhancedGitRef, isUserSetting?: boolean) => {
        if (isUserSetting) {
            this.isUserSetCompareBranch = true;
        }

        if (this.compareBranch) {
            const isDefault = this.defaultBranch ? this.defaultBranch.rootRef.name === this.compareBranch.rootRef.name : false;
            const oldCompareBranch: EnhancedGitRef = { ...this.compareBranch, isCompare: false, isDefault: isDefault };
            this.baseProviderInternal.update(oldCompareBranch);

            if (this.defaultBranch && this.defaultBranch.rootRef.name === oldCompareBranch.rootRef.name) {
                this.defaultBranch = { ...this.defaultBranch, isCompare: false };
            }
        }

        const newCompareBranch: EnhancedGitRef = { ...branch, isCompare: true };
        this.compareBranch = newCompareBranch;
        if (this.defaultBranch && this.defaultBranch.rootRef.name === this.compareBranch.rootRef.name) {
            this.defaultBranch = newCompareBranch;
        }
        this.baseProviderInternal.update(newCompareBranch);

        const statsObservablesByCommitId: { [key: string]: IObservableValue<GitBranchStats | undefined>[] } = {};
        const versionDescriptorsForBranches: GitVersionDescriptor[] = [];

        this.baseProviderInternal.modifyItems(item => {
            // Stats need to be recomputed below; void them out here
            // Kinda janky but gets the job done
            item.stats.value = undefined;

            // Update external stores for fetching of stats
            if (statsObservablesByCommitId[item.rootRef.objectId]) {
                statsObservablesByCommitId[item.rootRef.objectId].push(item.stats);
            } else {
                statsObservablesByCommitId[item.rootRef.objectId] = [item.stats];
            }
            versionDescriptorsForBranches.push({
                version: item.rootRef.objectId,
                versionType: GitVersionType.Commit,
                versionOptions: GitVersionOptions.None
            });

            let createPullRequestUrl: string | undefined;
            if (this.isTargetBranchUpdates2Enabled) {
                createPullRequestUrl = item.rootRef.name !== newCompareBranch.rootRef.name
                    ? this.routingService.getPullRequestCreateUrl(
                        this.repositoryName,
                        this.projectName,
                        refToBranchName(item.rootRef.name),
                        refToBranchName(newCompareBranch.rootRef.name)
                    ) : undefined;
            } else {
                createPullRequestUrl = item.createPullRequestUrl;
            }

            return {
                ...item,
                compareUrl: this.routingService.getBranchCompareUrl(
                    this.repositoryName,
                    this.projectName,
                    branchToVersionDescriptor(refToBranchName(newCompareBranch.rootRef.name)),
                    branchToVersionDescriptor(refToBranchName(item.rootRef.name))
                ),
                reverseCompareUrl: this.routingService.getBranchCompareUrl(
                    this.repositoryName,
                    this.projectName,
                    branchToVersionDescriptor(refToBranchName(item.rootRef.name)),
                    branchToVersionDescriptor(refToBranchName(newCompareBranch.rootRef.name))
                ),
                createPullRequestUrl: createPullRequestUrl
            };
        });

        // Fetch and update stats
        if (this.repository) {
            this.gitRestClient
                .getBranchStatsBatch(
                    {
                        baseCommit: {
                            version: newCompareBranch.rootRef.objectId,
                            versionType: GitVersionType.Commit,
                            versionOptions: GitVersionOptions.None
                        },
                        targetCommits: versionDescriptorsForBranches
                    },
                    this.repository.id,
                    this.projectName
                )
                .then(result => {
                    result.forEach(branchStat => {
                        if (statsObservablesByCommitId[branchStat.name]) {
                            statsObservablesByCommitId[branchStat.name].forEach(observable => (observable.value = branchStat));
                        }
                    });
                });
        }
    };

    public setDefaultBranch = (branch: EnhancedGitRef) => {
        if (this.defaultBranch) {
            const isCompare = this.compareBranch ? this.compareBranch.rootRef.name === this.defaultBranch.rootRef.name : false;
            const oldDefaultBranch: EnhancedGitRef = { ...this.defaultBranch, isDefault: false, isCompare: isCompare };
            this.baseProviderInternal.update(oldDefaultBranch);

            if (this.compareBranch && this.compareBranch.rootRef.name === oldDefaultBranch.rootRef.name) {
                this.compareBranch = { ...this.compareBranch, isDefault: false };
            }
        }

        const newDefaultBranch: EnhancedGitRef = { ...branch, isDefault: true };
        this.defaultBranch = newDefaultBranch;
        if (this.compareBranch && this.defaultBranch.rootRef.name === this.compareBranch.rootRef.name) {
            this.compareBranch = newDefaultBranch;
        }
        this.baseProviderInternal.update(newDefaultBranch);

        if (this.isTargetBranchUpdates2Enabled) {
            this.baseProviderInternal.modifyItems(item => {
                let createPullRequestUrl: string | undefined;
                if (this.compareBranch && item.rootRef.name !== this.compareBranch.rootRef.name) {
                    createPullRequestUrl = this.routingService.getPullRequestCreateUrl(
                        this.repositoryName,
                        this.projectName,
                        refToBranchName(item.rootRef.name),
                        refToBranchName(this.compareBranch.rootRef.name)
                    );
                }

                return { ...item, createPullRequestUrl: createPullRequestUrl };
            });
        } else {
            this.baseProviderInternal.modifyItems(item => {
                return {
                    ...item,
                    createPullRequestUrl: this.routingService.getPullRequestCreateUrl(
                        this.repositoryName,
                        this.projectName,
                        refToBranchName(newDefaultBranch.rootRef.name),
                        refToBranchName(item.rootRef.name)
                    )
                };
            });
        }

        // setting default also sets compare if not user set compare
        if (!this.isUserSetCompareBranch) {
            this.setCompareBranch(this.defaultBranch);
        }
    };

    public setFolderFavorite = (path: string, favoriteData: GitRefFavorite | undefined) => {
        path = branchNameToRef(path);

        if (this.folderPathToFavorite[path]) {
            this.folderPathToFavorite[path].value = favoriteData;
        } else {
            $DEBUG && console.warn("Setting a Folder Favorite for a folder that never requested a Favorite object; this is likely to fail");
            this.folderPathToFavorite[path] = new ObservableValue(favoriteData);
        }
    };
}
